import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Tag, Switch } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, MenuItem } from '@material-ui/core';
import CategoryCreateModalBody from './CategoryCreateModalBody';
import Modal from '../../component/Modal/Modal';
import NoxTable from '../../component/Table';
import InnerHeader from '../../component/InnerHeader';
import {
  fetchCategoriesAction,
  updateCategoryAction,
  setCategoryCreateDraft,
  setSelectedCategoryAction,
  createCategoryAction,
  deleteCategoryAction,
  cleanupAction
} from '../../actions/categoriesActions';
import CButton from '../../component/Button';
import { CStandardInput } from '../../component/Input';
import Loader from '../../component/Loader/Loader';
import formatDate from '../../helpers/formatDate';
import { DefaultSelect } from '../../component/Select';

const useStyles = makeStyles({
  garbage: {
    cursor: 'pointer',
    left: '8px',
    position: 'absolute',
    top: '0px'
  },
  gridItem: {
    textAlign: 'start',
    width: '100%'
  },
  gridItemSwitches: {
    alignItems: 'center',
    border: '1px solid #CAD8FF',
    borderRadius: '12px',
    display: 'flex',
    height: '40px',
    justifyContent: 'space-between',
    marginTop: '16px',
    padding: '11px',
    width: '100%'
  },
  multipleSelect: {
    marginTop: '16px',
    minWidth: '110px',
    width: '100%'
  },
  root: {
    ' & .MuiInputBase-root': {
      border: '1px solid #CAD8FF',
      borderRadius: '12px',
      color: 'black',
      height: '40px',
      padding: '14px 16px'
    },
    '& .MuiFormLabel-root.Mui-focused': {
      color: '#9BA0AD'
    },
    '& .MuiInput-inputMultiline': {
      alignItems: 'flex-start',
      height: '110px'
    },
    '& .MuiInput-underline:before': {
      display: 'none'
    },
    '& .MuiInputBase-input': {
      fontSize: '12px',
      padding: '0px'
    },

    '& .MuiInputLabel-formControl': {
      fontSize: '12px',
      left: '10px',
      top: '7px'
    },
    '& .MuiInputLabel-shrink': {
      fontFamily: 'Poppins',
      transform: 'translate(-10px, -10px) scale(0.83)'
    }
  },
  selectHeader: {
    color: '#9BA0AD',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%',
    margin: '0px',
    transform: 'translate(-30px, 10px) scale(0.83)'
  },
  switchHeader: {
    color: 'black',
    fontFamily: 'Poppins',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '100%',
    margin: '0px'
  }
});

const Categories = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const classes = useStyles();
  const [addCategoryModal, setAddCategoryModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const selectedCategory = useSelector(({ categories }) => categories.selectedCategory);

  const toggleDrawer = () => {
    setAddCategoryModal(!addCategoryModal);
  };

  const mapToTable = (content) => {
    const data = [];

    if (!content || content.length === 0) {
      return data;
    }

    content.forEach(({ _id, name, updatedAt, isEnabled, order, children, buyButton }, index) => {
      const date = formatDate(updatedAt);
      data.push({
        _id,
        buyButton,
        isEnabled,
        key: index,
        name,
        order,
        subCategories: children?.length ?? 0,
        updatedAt: date.toString() === 'Inv' ? '-' : date
      });
    });

    data.sort((a, b) => a.order - b.order);

    return data;
  };

  const columns = [
    {
      dataIndex: 'name',
      key: 'name',
      render: (name) => {
        return (
          <div
            style={{
              color: 'black',
              fontWeight: 700
            }}
          >
            {name}
          </div>
        );
      },
      sorter: (a, b) => a.name.localeCompare(b.name),
      title: 'Name'
    },
    {
      dataIndex: 'subCategories',
      key: 'subCategories',
      sorter: (a, b) => a.subCategories - b.subCategories,
      title: 'Sub-Categories'
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
      title: 'Updated At'
    },
    {
      dataIndex: 'isEnabled',
      key: 'isEnabled',
      render: (isEnabled, category) => {
        return (
          <Switch
            defaultChecked={isEnabled}
            onChange={(isEnabled) => {
              const selectedCategory = categories.filter((c) => c._id === category._id)[0];
              dispatch(
                updateCategoryAction(
                  category._id,
                  {
                    ...selectedCategory,
                    isEnabled
                  },
                  () => {
                    dispatch(fetchCategoriesAction());
                  }
                )
              );
            }}
          />
        );
      },
      sorter: (a, b) => a.isEnabled.toString().localeCompare(b.isEnabled.toString()),
      title: 'Enable'
    },
    {
      dataIndex: 'buyButton',
      key: 'buyButton',
      render: (buyButton, category) => {
        return (
          <Switch
            defaultChecked={buyButton}
            onChange={(buyButton) => {
              const selectedCategory = categories.filter((c) => c._id === category._id)[0];
              dispatch(
                updateCategoryAction(
                  category._id,
                  {
                    ...selectedCategory,
                    buyButton
                  },
                  () => {
                    dispatch(fetchCategoriesAction());
                  }
                )
              );
            }}
          />
        );
      },
      sorter: (a, b) => a.buyButton.toString().localeCompare(b.buyButton.toString()),
      title: 'Buy Button'
    },
    {
      key: 'edit',
      render: (category) => <Tag onClick={() => openDetail(category)}>Edit</Tag>
    }
  ];

  const deleteCategory = () => {
    selectedRowKeys.forEach((index) => {
      const item = data[index];

      dispatch(
        deleteCategoryAction(item._id, () => {
          dispatch(fetchCategoriesAction());
        })
      );
    });

    setDeleteModal(false);
    setSelectedRowKeys([]);
  };

  const openDetail = (item) => {
    history.push({
      pathname: `/ecommerce/categories/${item._id}`
    });
  };

  const actions = [
    {
      onClick: toggleDrawer,
      title: 'Add New Category',
      type: 'primary'
    }
  ];

  if (selectedRowKeys.length) {
    actions.unshift({
      onClick: () => {
        setDeleteModal(true);
      },
      title: 'Delete',
      type: 'secondary'
    });
  }

  useEffect(() => {
    async function load() {
      dispatch(fetchCategoriesAction());
    }

    load();
    return dispatch(cleanupAction());
  }, [dispatch]);

  const isLoaded = useSelector(({ categories }) => categories.isLoaded);
  const categories = useSelector(({ categories }) => categories.categories);
  const categoryCreateDraft = useSelector(({ categories }) => categories.categoryCreateDraft);

  const data = mapToTable(categories);

  const options = ['material', 'color', 'size', 'age', 'quantity'];

  if (!isLoaded) {
    return <Loader />;
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 165px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={categories.length} title="Categories" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={data}
        // onRowClick={openDetail}
        dragAndDropUrl="category"
        isDragAndDropEnabled
        loading={!isLoaded}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
      />
      <Modal
        footerActions={
          <>
            <CButton onClick={() => setAddCategoryModal(false)} type="secondary">
              Cancel
            </CButton>
            <CButton onClick={deleteCategory} type="primary">
              Delete
            </CButton>
          </>
        }
        handleClose={() => setDeleteModal(false)}
        isModalOpen={deleteModal}
        styles={{
          '& .MuiDialogContent-root': {
            paddingBottom: '24px',
            paddingTop: '5px'
          },
          width: '400px'
        }}
        titleContent={<p className="modal-title">New Category</p>}
      >
        Are You Sure To Delete Selected Category(ies)
      </Modal>

      <Modal
        footerActions={
          <>
            <CButton
              onClick={() => {
                setAddCategoryModal(false);
                dispatch(setSelectedCategoryAction(null));
              }}
              type="secondary"
            >
              Cancel
            </CButton>
            {selectedCategory?.name && (
              <CButton
                onClick={() => {
                  dispatch(
                    createCategoryAction(categoryCreateDraft, () => {
                      dispatch(fetchCategoriesAction());
                    })
                  );
                  setAddCategoryModal(false);
                  dispatch(setSelectedCategoryAction(null));
                }}
                type="primary"
              >
                Create
              </CButton>
            )}
          </>
        }
        handleClose={() => {
          setAddCategoryModal(false);
          dispatch(setSelectedCategoryAction(null));
        }}
        isModalOpen={addCategoryModal}
        styles={{
          '& .MuiDialogContent-root': {
            display: 'flex',
            flexDirection: 'column',
            height: selectedCategory?.name ? '480px' : 'auto',
            justifyContent: 'space-evenly',
            paddingBottom: selectedCategory?.name ? '8px' : '24px',
            paddingTop: '0px'
          },
          width: '600px'
        }}
        titleContent={<p className="modal-title">New Category</p>}
      >
        {selectedCategory?.name ? (
          <>
            <CStandardInput
              disableUnderline
              formclasses={{ root: classes.root }}
              htmlFor="standard-input-user-email"
              id="standard-input-user-email"
              label="Category Name"
              onChange={(e) => {
                dispatch(
                  setCategoryCreateDraft({
                    ...categoryCreateDraft,
                    categoryName: e.target.value
                  })
                );
              }}
              size="large"
              value={categoryCreateDraft?.categoryName ?? ''}
              width="true"
            />
            <CStandardInput
              disableUnderline
              formclasses={{ root: classes.root }}
              htmlFor="standard-input-user-email-1"
              id="standard-input-user-email-1"
              label="Category Name On Georgian"
              onChange={(e) => {
                dispatch(
                  setCategoryCreateDraft({
                    ...categoryCreateDraft,
                    categoryNameGe: e.target.value
                  })
                );
              }}
              size="large"
              value={categoryCreateDraft?.categoryNameGe ?? ''}
              width="true"
            />
          </>
        ) : null}

        <div className={classes.gridItem}>
          <p className={classes.selectHeader}>Parent Category</p>
          <DefaultSelect
            onSelect={(categoryName) => {
              dispatch(
                setSelectedCategoryAction(
                  [
                    {
                      descriptors: {
                        brand: false,
                        condition: false,
                        gender: false,
                        options: []
                      },
                      name: '- Parent Category',
                      parentId: ''
                    },
                    ...categories
                  ].filter((category) => category.name === categoryName)[0]
                )
              );
            }}
            selectStyles={{ marginTop: '16px' }}
            value={selectedCategory?.name || ''}
          >
            <MenuItem key="Category" value="">
              Category
            </MenuItem>

            {[{ name: '- Parent Category' }, ...categories].map((category) => {
              return (
                <MenuItem key={category.name} value={category.name}>
                  {category.name}
                </MenuItem>
              );
            })}
          </DefaultSelect>
        </div>

        {selectedCategory?.name ? <CategoryCreateModalBody options={options} /> : null}
      </Modal>
    </div>
  );
};

export default Categories;
